import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { 
    CustomerReservationList, 
    ReservationActionState,
    CustomerOffer 
} from '../../../_models/reservation.model';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MenuStateService } from '../../../_services/app/menu-state.service';
import { NotificationService } from '../../../_services/app/notification.service';
import { Router } from '@angular/router';
import { OfferUtilsService } from '../../../_utils/offer-utils.service';
import { ReservationActionsUtilsService } from '../../../_utils/reservation-actions-utils.service';
import { ClientReservationService } from '../../../_services/api/reservation-client.service';

@Component({
  selector: 'app-member-reservation-card',
  imports: [CommonModule],
  templateUrl: './member-reservation-card.html',
  styleUrl: './member-reservation-card.scss',
})
export class MemberReservationCard implements OnInit, OnDestroy {
  @Input() reservation!: CustomerReservationList;
  @Output() reservationUpdated = new EventEmitter<void>();
  @Output() reservationDeleted = new EventEmitter<void>();
  
  menuOpen = false;
  showDeleteModal = false;
  showCancelModal = false;
  showRejectModal = false;
  isUpdatingVisibility = false;
  isPerformingAction = false;
  
  actionState!: ReservationActionState;
  selectedOfferId?: number;
  actionType: 'accept' | 'reject' | 'request_new' | 'cancel' | 'delete' | null = null;

  constructor(
    private menuStateService: MenuStateService,
    private notification: NotificationService,
    private elementRef: ElementRef,
    private router: Router,
    private offerUtils: OfferUtilsService,
    private reservationActionsUtils: ReservationActionsUtilsService,
    private reservationService: ClientReservationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    this.menuStateService.activeMenu$.subscribe(activeId => {
      this.menuOpen = activeId === this.reservation.id;
    });
    if (isPlatformBrowser(this.platformId)) {
        document.addEventListener('click', this.closeMenu.bind(this));
    }
    
    // Initialize action state
    this.updateActionState();
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
        document.removeEventListener('click', this.closeMenu.bind(this));
    }
  }

  /**
   * Update the action state based on reservation status
   */
  private updateActionState(): void {
    this.actionState = this.reservationActionsUtils.getReservationActionState(this.reservation);
    
    // Get the first pending offer for accept/reject actions
    const pendingOffer = this.reservationActionsUtils.getFirstPendingOffer(this.reservation);
    if (pendingOffer) {
      this.selectedOfferId = pendingOffer.id;
    }
  }

  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    } else {
      this.menuStateService.setActiveMenu(this.reservation.id);
    }
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    }
  }

  formatAddress(): string {
    if (!this.reservation.address) return 'Non spécifiée';
    
    const parts: string[] = [];
    
    if (this.reservation.address.streetNumber && this.reservation.address.street) {
      parts.push(`${this.reservation.address.street} ${this.reservation.address.streetNumber}`);
    } else if (this.reservation.address.street) {
      parts.push(this.reservation.address.street);
    }
    
    if (this.reservation.address.postalCode && this.reservation.address.city) {
      parts.push(`${this.reservation.address.postalCode} ${this.reservation.address.city}`);
    } else if (this.reservation.address.postalCode) {
      parts.push(this.reservation.address.postalCode);
    } else if (this.reservation.address.city) {
      parts.push(this.reservation.address.city);
    }
    
    if (parts.length === 0) {
      return 'Non spécifiée';
    }
    
    return parts.join(', ') + ".";
  }

  details() {
    this.router.navigate(['/member/reservations/details', this.reservation.id]);
  }

  // Action methods
  acceptOffer(): void {
    if (!this.selectedOfferId) {
      this.notification.showError('Aucune offre en attente à accepter');
      return;
    }

    this.actionType = 'accept';
    this.menuOpen = false;
    this.isPerformingAction = true;

    this.reservationService.acceptOffer(this.reservation.id, this.selectedOfferId)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(response.message || 'Offre acceptée avec succès');
          this.reservationUpdated.emit();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de l\'acceptation de l\'offre');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  rejectOffer(): void {
    if (!this.selectedOfferId) {
      this.notification.showError('Aucune offre en attente à refuser');
      return;
    }

    this.actionType = 'reject';
    this.menuOpen = false;
    this.showRejectModal = true;
  }

  confirmRejectOffer(): void {
    if (!this.selectedOfferId) return;

    this.isPerformingAction = true;
    this.showRejectModal = false;

    this.reservationService.rejectOffer(this.reservation.id, this.selectedOfferId)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(response.message || 'Offre refusée avec succès');
          this.reservationUpdated.emit();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors du refus de l\'offre');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  requestNewOffer(): void {
    this.actionType = 'request_new';
    this.menuOpen = false;
    this.isPerformingAction = true;

    this.reservationService.requestNewOffer(this.reservation.id)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(response.message || 'Nouvelle offre demandée avec succès');
          this.reservationUpdated.emit();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de la demande de nouvelle offre');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  updateReservation(): void {
    this.menuOpen = false;
    this.router.navigate(['/member/reservations/edit', this.reservation.id]);
  }

  feedbackDetails(){
    this.menuOpen = false;
    this.router.navigate(['/member/reservations', this.reservation.id, 'feedback', 'details']);
  }

  cancelReservation(): void {
    this.actionType = 'cancel';
    this.menuOpen = false;
    this.showCancelModal = true;
  }

  confirmCancelReservation(): void {
    this.isPerformingAction = true;
    this.showCancelModal = false;

    this.reservationService.cancelReservation(this.reservation.id)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(response.message || 'Réservation annulée avec succès');
          this.reservationDeleted.emit();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de l\'annulation de la réservation');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  deleteReservation(): void {
    this.actionType = 'delete';
    this.menuOpen = false;
    this.showDeleteModal = true;
  }

  confirmDeleteReservation(): void {
    this.isPerformingAction = true;
    this.showDeleteModal = false;

    this.reservationService.deleteReservation(this.reservation.id)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(response.message || 'Réservation supprimée avec succès');
          this.reservationDeleted.emit();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de la suppression de la réservation');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.actionType = null;
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
    this.actionType = null;
  }

  closeRejectModal(): void {
    this.showRejectModal = false;
    this.actionType = null;
  }

  getActionButtonText(action: string): string {
    return this.reservationActionsUtils.getActionButtonText(action);
  }

  getActionButtonClass(action: string): string {
    return this.reservationActionsUtils.getActionButtonClass(action);
  }

  getActionButtonIcon(action: string): string {
    return this.reservationActionsUtils.getActionButtonIcon(action);
  }

  getReservationAdminStatusClass(): string {
    return this.offerUtils.getReservationOverallStatusClass(
      this.reservation.reservationClientStatus, 
      this.reservation.reservationAdminStatus
    );
  }

  getReservationAdminStatusText(): string {
    return this.offerUtils.getReservationOverallStatusText(
      this.reservation.reservationClientStatus, 
      this.reservation.reservationAdminStatus
    );
  }

  getLoadingText(): string {
    switch (this.actionType) {
      case 'accept': return 'Acceptation en cours...';
      case 'reject': return 'Refus en cours...';
      case 'request_new': return 'Demande en cours...';
      case 'cancel': return 'Annulation en cours...';
      case 'delete': return 'Suppression en cours...';
      default: return 'Traitement en cours...';
    }
  }
}